import React, { useEffect, useRef, useState } from 'react'

import * as S from './styles'
import { TextProps } from '../text'
import { getRandomElement } from '@/utils/randomElement'

export type TypewriterProps = {
  /**
   * Array of texts to be displayed
   *
   * @example
   * ```tsx
   * <Typewriter texts={['Hello', 'World']} />
   * ```
   *
   */
  texts: Array<string>

  /**
   * The size of the text
   *
   * @example
   * ```tsx
   * <Typewriter size="h1" />
   * ```
   *
   * @default 'h1'
   *
   */
  size?: TextProps['size']
}

export const Typewriter: React.FC<TypewriterProps> = ({
  texts,
  size = 'h1',
}) => {
  const [displayText, setDisplayText] = useState('')

  const wordRef = useRef<string>('')

  useEffect(() => {
    const getRandomWord = () => {
      const filteredTexts = texts.filter((text) => text !== wordRef.current)

      if (!filteredTexts.length) return wordRef.current

      return getRandomElement(filteredTexts)
    }

    wordRef.current = getRandomWord()
    let char = 0
    let completedWord = false
    let timeout: NodeJS.Timeout

    const interval = setInterval(() => {
      if (char > wordRef.current.length) {
        setTimeout(() => {
          completedWord = true
        }, 500)
      }

      if (completedWord) {
        char -= 1

        if (char < 0) {
          completedWord = false
          char = 1
          wordRef.current = getRandomWord()
          return
        }

        setDisplayText(wordRef.current.slice(0, char))
        return
      }

      setDisplayText(wordRef.current.slice(0, char))
      char += 1
    }, 150)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [texts])

  return (
    <S.TypewriterEffect size={size} color="black">
      {displayText}
    </S.TypewriterEffect>
  )
}
